"use client";
import React, { useState, useRef, useEffect, useContext } from "react";
import ReactDOM from "react-dom";
import {
  Bell, Settings, Menu, LogOut, User, ChevronDown, X, Search,
  Home, ShoppingCart, Package, Users, Tag, BarChart3, HelpCircle,
  MessageSquare, Box, MapPin, Building2, Building, List,
  Palette, Truck, Map, Sparkles, ReceiptIndianRupee, Phone,
  LayoutGrid, Grid3X3, FlaskConical, Ruler, Image, Wand2, Navigation,
  Receipt, CircleDollarSign, ClipboardList, CreditCard, ShoppingBag,
  Star, Percent, Ticket, AlertCircle,
} from "lucide-react";
import GlobalSearch from "./GlobalSearch";
import { useNavigate, useLocation } from "react-router-dom";
import LogoutModal from "./LogoutModal";
import { useAppContext } from "../App";
import { NotificationContext } from "./context/NotificationContext";
import { useSettings } from "./context/SettingsContext";
import { CDN_URL } from "./config/apiconfig";

// ── Route → page title/icon map ───────────────────────────────────────────────
const ROUTE_MAP = {
  "/dashboard":          { label: "Dashboard",              icon: Home },
  "/categories":         { label: "Categories",             icon: LayoutGrid },
  "/subcategories":      { label: "Sub-Categories",         icon: Grid3X3 },
  "/modals":             { label: "Models",                 icon: Box },
  "/products/add":       { label: "Add Product",            icon: Package },
  "/products/edit":      { label: "Edit Product",           icon: Package },
  "/products":           { label: "Products",               icon: Package },
  "/producttype":        { label: "Product Types",          icon: Tag },
  "/productdetails":     { label: "Product Details",        icon: Package },
  "/productattribute":   { label: "Product Attributes",     icon: Palette },
  "/product-attributes/materials":            { label: "Materials",            icon: FlaskConical },
  "/product-attributes/colours":              { label: "Colours",              icon: Palette },
  "/product-attributes/shapes":               { label: "Shapes",               icon: Sparkles },
  "/product-attributes/Sizes":                { label: "Sizes",                icon: Ruler },
  "/product-attributes/Banners":              { label: "Banners",              icon: Image },
  "/product-attributes/customization-images": { label: "Customization Images", icon: Wand2 },
  "/suppliers":          { label: "Suppliers",              icon: Truck },
  "/states":             { label: "States",                 icon: MapPin },
  "/districts":          { label: "Districts",              icon: Map },
  "/mandals":            { label: "Mandals",                icon: Navigation },
  "/cities":             { label: "Cities",                 icon: Building2 },
  "/expenses":           { label: "Expenses",               icon: Receipt },
  "/items":              { label: "Items",                  icon: List },
  "/currency-settings":  { label: "Currency Settings",      icon: CircleDollarSign },
  "/expensevouchers":    { label: "Expense Vouchers",       icon: Receipt },
  "/payment-voucher":    { label: "Payment Vouchers",       icon: CreditCard },
  "/create-payment":     { label: "Create Payment Voucher", icon: CreditCard },
  "/purchase-voucher":   { label: "Purchase Vouchers",      icon: ShoppingBag },
  "/create-purchase":    { label: "Create Purchase Voucher",icon: ShoppingBag },
  "/orders":             { label: "Orders",                 icon: ShoppingCart },
  "/order-types":        { label: "Order Types",            icon: ClipboardList },
  "/transactions":       { label: "Transactions",           icon: CreditCard },
  "/customers":          { label: "Customers",              icon: Users },
  "/customers-coming-soon": { label: "Customer Overview",   icon: Home },
  "/reviews":            { label: "Reviews",                icon: Star },
  "/faqs":               { label: "FAQs",                   icon: HelpCircle },
  "/coupons":            { label: "Coupons",                icon: Ticket },
  "/discount":           { label: "Discount Types",         icon: Percent },
  "/notification":       { label: "Notifications",          icon: Bell },
  "/contact":            { label: "Contact Management",     icon: Phone },
  "/issue-types":        { label: "Issue Types",            icon: AlertCircle },
  "/issues":             { label: "Issues & Suggestions",   icon: MessageSquare },
  "/business-reports":   { label: "Business Reports",       icon: BarChart3 },
  "/tax":                { label: "Tax Settings",           icon: ReceiptIndianRupee },
  "/settings":           { label: "Settings",               icon: Settings },
};

const getPageInfo = (pathname) => {
  if (ROUTE_MAP[pathname]) return ROUTE_MAP[pathname];
  for (const [path, info] of Object.entries(ROUTE_MAP)) {
    if (pathname.startsWith(path + "/") || pathname === path) return info;
  }
  return { label: "Dashboard", icon: Home };
};

// ── Portal dropdown — escapes all stacking contexts ──────────────────────────
// Uses ReactDOM.createPortal so it renders directly in document.body.
// Navigation uses the navigate() function passed as a prop (not <Link>)
// so it works correctly outside the normal React tree position.
const PortalDropdown = ({ triggerRef, children, onClose }) => {
  const [pos, setPos] = useState({ top: 0, right: 0 });

  const calcPos = () => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
  };

  useEffect(() => {
    calcPos();
    window.addEventListener("resize", calcPos);
    window.addEventListener("scroll", calcPos, true);
    return () => {
      window.removeEventListener("resize", calcPos);
      window.removeEventListener("scroll", calcPos, true);
    };
  }, []);

  // Close on outside click — check both the trigger and the dropdown itself
  useEffect(() => {
    const handler = (e) => {
      if (triggerRef.current?.contains(e.target)) return; // clicking trigger toggles, not this handler's job
      const dropdown = document.getElementById("portal-dropdown-root");
      if (dropdown && dropdown.contains(e.target)) return;
      onClose();
    };
    // slight delay so the triggering click doesn't immediately close
    const t = setTimeout(() => document.addEventListener("mousedown", handler), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", handler);
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      id="portal-dropdown-root"
      style={{
        position: "fixed",
        top: pos.top,
        right: pos.right,
        zIndex: 2147483647, // max z-index
        pointerEvents: "auto",
      }}
    >
      {children}
    </div>,
    document.body
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const DashboardHeader = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const context   = useAppContext();
  const { adminProfile: profile } = useSettings();
  const { isMobileMenuOpen, setIsMobileMenuOpen } = context || {};
  const { notifications = [] } = useContext(NotificationContext);

  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu,      setShowUserMenu]      = useState(false);
  const [showLogoutModal,   setShowLogoutModal]   = useState(false);
  const [showSearch,        setShowSearch]        = useState(false);

  const bellRef = useRef(null);
  const userRef = useRef(null);

  // Close dropdowns on route change
  useEffect(() => {
    setShowNotifications(false);
    setShowUserMenu(false);
  }, [location.pathname]);

  // Ctrl+K / Cmd+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Navigate and close everything
  const goTo = (path) => {
    setShowUserMenu(false);
    setShowNotifications(false);
    setIsMobileMenuOpen?.(false);
    // Use setTimeout to ensure state updates flush before navigation
    setTimeout(() => navigate(path), 0);
  };

  // Navigate to the notifications page, pre-filtered to a single notification
  const openNotification = (n) => {
    setShowUserMenu(false);
    setShowNotifications(false);
    setIsMobileMenuOpen?.(false);
    setTimeout(() => {
      navigate("/notification", { state: { notificationId: n.id } });
    }, 0);
  };

  // Open logout modal and close dropdown
  const openLogout = () => {
    setShowUserMenu(false);
    setShowNotifications(false);
    setTimeout(() => setShowLogoutModal(true), 0);
  };

  // Derived
  const unreadCount = notifications.filter((n) => n.unread).length;
  const adminName = profile
    ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim()
    : localStorage.getItem("admin_name") || "Admin";
  const initials = adminName.split(" ").filter(Boolean).map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "A";
  const { label: pageTitle, icon: PageIcon } = getPageInfo(location.pathname);
  const adminRole = localStorage.getItem("role") || "admin";

  return (
    <>
      {/* ── Header bar ── */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 gap-4">

          {/* Left */}
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setIsMobileMenuOpen?.(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors flex-shrink-0"
              aria-label="Toggle menu"
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="lg:hidden flex items-center gap-2 flex-shrink-0">
              <div className="w-8 h-8 bg-gradient-to-br from-teal-600 to-teal-800 rounded-lg flex items-center justify-center">
                <img
                  src="https://cdn.acsdev.in/ManaPrint/manaprint_logo_tras.png"
                  alt="M"
                  className="w-6 h-6 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = "block";
                  }}
                />
                <span style={{ display: "none" }} className="text-white text-sm font-bold">M</span>
              </div>
              <span className="text-base font-bold text-teal-800 tracking-tight">ManaPrint</span>
            </div>

            <div className="hidden lg:flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                <PageIcon className="w-4 h-4 text-teal-600" />
              </div>
              <div className="min-w-0">
                <h1 className="text-base font-semibold text-gray-800 leading-tight truncate">{pageTitle}</h1>
                <p className="text-xs text-gray-400 leading-tight truncate hidden xl:block">ManaPrint Admin Panel</p>
              </div>
            </div>
          </div>

          {/* Centre search */}
          <button
            onClick={() => setShowSearch(true)}
            className="hidden md:flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-400 hover:border-teal-400 hover:bg-white transition-colors w-56 lg:w-72"
          >
            <Search className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1 text-left">Search anything…</span>
            <kbd className="hidden lg:flex items-center gap-0.5 text-[10px] text-gray-300 border border-gray-200 rounded px-1 bg-white font-mono">⌘K</kbd>
          </button>

          {/* Right */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={() => setShowSearch(true)}
              className="md:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
              aria-label="Search"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Bell */}
            <button
              ref={bellRef}
              onClick={() => { setShowNotifications((s) => !s); setShowUserMenu(false); }}
              className={`relative p-2 rounded-lg transition-colors ${showNotifications ? "bg-gray-100 text-teal-700" : "text-gray-500 hover:bg-gray-100"}`}
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5 leading-none">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>

            {/* Settings */}
            <button
              onClick={() => goTo("/settings")}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors hidden sm:flex"
              aria-label="Settings"
            >
              <Settings className="w-5 h-5" />
            </button>

            <div className="w-px h-5 bg-gray-200 mx-1 hidden sm:block" />

            {/* Profile */}
            <button
              ref={userRef}
              onClick={() => { setShowUserMenu((s) => !s); setShowNotifications(false); }}
              className={`flex items-center gap-2.5 px-2 py-1.5 rounded-xl transition-colors ${showUserMenu ? "bg-gray-100" : "hover:bg-gray-50"}`}
              aria-label="User menu"
            >
              <div className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center flex-shrink-0 ring-2 ring-teal-100">
                {profile?.image ? (
                  <img src={`${CDN_URL}/${profile.image}`} alt="Profile" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                ) : (
                  <span className="text-white text-xs font-bold">{initials}</span>
                )}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-semibold text-gray-800 leading-tight truncate max-w-[120px]">{adminName}</p>
                <p className="text-xs text-gray-400 leading-tight capitalize">{adminRole}</p>
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 hidden md:block ${showUserMenu ? "rotate-180" : ""}`} />
            </button>
          </div>
        </div>
      </header>

      {/* ── Notifications portal ── */}
      {showNotifications && (
        <PortalDropdown triggerRef={bellRef} onClose={() => setShowNotifications(false)}>
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden" style={{ width: 360 }}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-teal-600" />
                <h3 className="text-sm font-semibold text-gray-800">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="text-xs font-semibold text-white bg-red-500 rounded-full px-2 py-0.5">{unreadCount}</span>
                )}
              </div>
              <button onClick={() => setShowNotifications(false)} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
              {notifications.length === 0 ? (
                <div className="py-12 text-center">
                  <Bell className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">All caught up!</p>
                </div>
              ) : (
                notifications.slice(0, 12).map((n) => (
                  <div
                    key={n.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openNotification(n)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openNotification(n);
                      }
                    }}
                    className={`flex items-start gap-3 px-5 py-3.5 hover:bg-gray-50 transition-colors cursor-pointer ${n.unread ? "bg-teal-50/50" : ""}`}
                  >
                    <span className={`mt-2 w-2 h-2 rounded-full flex-shrink-0 ${n.unread ? "bg-teal-500" : "bg-gray-300"}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{n.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-xs text-gray-400 mt-1">{n.time}</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => goTo("/notification")}
                className="w-full text-center text-sm text-teal-600 hover:text-teal-700 font-medium transition-colors"
              >
                View all notifications →
              </button>
            </div>
          </div>
        </PortalDropdown>
      )}

      {/* ── User menu portal ── */}
      {showUserMenu && (
        <PortalDropdown triggerRef={userRef} onClose={() => setShowUserMenu(false)}>
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden" style={{ width: 256 }}>

            {/* Profile summary */}
            <div className="bg-gradient-to-br from-teal-700 to-teal-800 px-4 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-white/20 flex items-center justify-center flex-shrink-0 ring-2 ring-white/30">
                  {profile?.image ? (
                    <img src={`${CDN_URL}/${profile.image}`} alt="Profile" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <span className="text-white font-bold text-sm">{initials}</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-white truncate">{adminName}</p>
                  <p className="text-xs text-teal-200 truncate mt-0.5">{profile?.email || "Admin"}</p>
                  <span className="inline-flex mt-1 px-2 py-0.5 rounded-full bg-white/20 text-white text-[10px] font-medium capitalize">{adminRole}</span>
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="py-1.5">
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); goTo("/settings"); }}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors w-full text-left"
              >
                <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center">
                  <User className="w-3.5 h-3.5 text-gray-500" />
                </div>
                <span>Profile Settings</span>
              </button>

              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); goTo("/settings"); }}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors w-full text-left sm:hidden"
              >
                <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Settings className="w-3.5 h-3.5 text-gray-500" />
                </div>
                <span>Settings</span>
              </button>

              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); goTo("/notification"); }}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors w-full text-left"
              >
                <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center relative">
                  <Bell className="w-3.5 h-3.5 text-gray-500" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-red-500 rounded-full text-[8px] text-white flex items-center justify-center font-bold">{unreadCount}</span>
                  )}
                </div>
                <span>Notifications</span>
                {unreadCount > 0 && <span className="ml-auto text-xs font-medium text-red-500">{unreadCount} new</span>}
              </button>
            </div>

            {/* Sign out */}
            <div className="border-t border-gray-100 py-1.5">
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); openLogout(); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center">
                  <LogOut className="w-3.5 h-3.5 text-red-500" />
                </div>
                <span className="font-medium">Sign Out</span>
              </button>
            </div>
          </div>
        </PortalDropdown>
      )}

      {showLogoutModal && (
        <LogoutModal isOpen={showLogoutModal} onClose={() => setShowLogoutModal(false)} />
      )}

      <GlobalSearch open={showSearch} onClose={() => setShowSearch(false)} />
    </>
  );
};

export default DashboardHeader;